import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { getUser, logout } from '../utils/auth';
import { 
  Users, GraduationCap, UserPlus, LogOut, Layers, BookOpen, 
  Plus, Trash2, ToggleLeft, ToggleRight, Search, ChevronDown, Pencil, KeyRound
} from 'lucide-react';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const user = getUser();
  const [activeTab, setActiveTab] = useState('teachers');
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [boards, setBoards] = useState([]);
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [createUserType, setCreateUserType] = useState('teacher');
  const [searchTerm, setSearchTerm] = useState('');

  // Form data for creating users
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'teacher',
    board_id: '',
    grade_id: '',
    subject_ids: [],
    board_ids: [],
    grade_ids: [],
  });

  // Form data for editing users
  const [editFormData, setEditFormData] = useState({
    name: '',
    board_id: '',
    grade_id: '',
    subject_ids: [],
    board_ids: [],
    grade_ids: [],
  });

  // Reset password modal state
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const fetchData = async () => {
    try {
      const [teachersRes, studentsRes, boardsRes, gradesRes, subjectsRes] = await Promise.all([
        api.get('/admin/teachers'),
        api.get('/admin/students'),
        api.get('/boards'),
        api.get('/grades'),
        api.get('/subjects'),
      ]);
      setTeachers(teachersRes.data);
      setStudents(studentsRes.data);
      setBoards(boardsRes.data);
      setGrades(gradesRes.data);
      setSubjects(subjectsRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const openCreateModal = (type) => {
    setCreateUserType(type);
    setFormData({
      email: '',
      password: '',
      name: '',
      role: type,
      board_id: '',
      grade_id: '',
      subject_ids: [],
      board_ids: [],
      grade_ids: [],
    });
    setShowCreateModal(true);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    const loadingToast = toast.loading(`Creating ${createUserType}...`);
    
    try {
      await api.post('/admin/users', formData);
      toast.dismiss(loadingToast);
      toast.success(`${createUserType === 'teacher' ? 'Teacher' : 'Student'} created successfully!`);
      setShowCreateModal(false);
      fetchData();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(err.response?.data?.detail || 'Failed to create user');
    }
  };

  const toggleUserActive = async (userId, currentStatus) => {
    try {
      await api.patch(`/admin/users/${userId}/toggle-active`);
      toast.success(`User ${currentStatus ? 'deactivated' : 'activated'}`);
      fetchData();
    } catch (err) {
      toast.error('Failed to update user status');
    }
  };

  const deleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}? This cannot be undone.`)) return;
    
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('User deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete user');
    }
  };

  const openEditModal = (userToEdit) => {
    setEditingUser(userToEdit);
    setEditFormData({
      name: userToEdit.name || '',
      board_id: userToEdit.board_id || '',
      grade_id: userToEdit.grade_id || '',
      subject_ids: userToEdit.subject_ids || [],
      board_ids: userToEdit.board_ids || [],
      grade_ids: userToEdit.grade_ids || [],
    });
    setShowEditModal(true);
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    const loadingToast = toast.loading('Updating user...');
    
    try {
      await api.patch(`/admin/users/${editingUser.id}`, editFormData);
      toast.dismiss(loadingToast);
      toast.success('User updated successfully!');
      setShowEditModal(false);
      setEditingUser(null);
      fetchData();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleEditSubjectToggle = (subjectId) => {
    setEditFormData(prev => {
      const currentIds = prev.subject_ids;
      if (currentIds.includes(subjectId)) {
        return { ...prev, subject_ids: currentIds.filter(id => id !== subjectId) };
      } else {
        return { ...prev, subject_ids: [...currentIds, subjectId] };
      }
    });
  };

  const handleEditMultiSelect = (field, itemId) => {
    setEditFormData(prev => {
      const currentIds = prev[field];
      if (currentIds.includes(itemId)) {
        return { ...prev, [field]: currentIds.filter(id => id !== itemId) };
      } else {
        return { ...prev, [field]: [...currentIds, itemId] };
      }
    });
  };

  const openResetPasswordModal = (userToReset) => {
    setResetPasswordUser(userToReset);
    setNewPassword('');
    setShowResetPasswordModal(true);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    const loadingToast = toast.loading('Resetting password...');
    
    try {
      await api.patch(`/admin/users/${resetPasswordUser.id}/reset-password`, {
        new_password: newPassword
      });
      toast.dismiss(loadingToast);
      toast.success(`Password reset for ${resetPasswordUser.name}`);
      setShowResetPasswordModal(false);
      setResetPasswordUser(null);
      setNewPassword('');
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleSubjectToggle = (subjectId) => {
    setFormData(prev => {
      const currentIds = prev.subject_ids;
      if (currentIds.includes(subjectId)) {
        return { ...prev, subject_ids: currentIds.filter(id => id !== subjectId) };
      } else {
        return { ...prev, subject_ids: [...currentIds, subjectId] };
      }
    });
  };

  const handleMultiSelect = (field, itemId) => {
    setFormData(prev => {
      const currentIds = prev[field];
      if (currentIds.includes(itemId)) {
        return { ...prev, [field]: currentIds.filter(id => id !== itemId) };
      } else {
        return { ...prev, [field]: [...currentIds, itemId] };
      }
    });
  };

  const filteredTeachers = teachers.filter(t => 
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredStudents = students.filter(s => 
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Admin Dashboard</h1>
            <p className="text-sm text-slate-600">Welcome, {user?.name}</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 transition-all"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6 md:p-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl border border-slate-100">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-indigo-100 rounded-lg">
                <GraduationCap className="w-6 h-6 text-indigo-700" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Teachers</p>
                <p className="text-2xl font-bold text-slate-900">{teachers.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl border border-slate-100">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <Users className="w-6 h-6 text-green-700" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Students</p>
                <p className="text-2xl font-bold text-slate-900">{students.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl border border-slate-100">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-amber-100 rounded-lg">
                <Layers className="w-6 h-6 text-amber-700" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Boards</p>
                <p className="text-2xl font-bold text-slate-900">{boards.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl border border-slate-100">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <BookOpen className="w-6 h-6 text-purple-700" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Subjects</p>
                <p className="text-2xl font-bold text-slate-900">{subjects.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-4 mb-6 border-b border-slate-200">
          <button
            onClick={() => setActiveTab('teachers')}
            className={`px-4 py-3 font-medium transition-all border-b-2 -mb-px ${
              activeTab === 'teachers'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Teachers ({teachers.length})
          </button>
          <button
            onClick={() => setActiveTab('students')}
            className={`px-4 py-3 font-medium transition-all border-b-2 -mb-px ${
              activeTab === 'students'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Students ({students.length})
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`px-4 py-3 font-medium transition-all border-b-2 -mb-px ${
              activeTab === 'settings'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Boards/Grades/Subjects
          </button>
        </div>

        {/* Actions Bar */}
        {activeTab !== 'settings' && (
          <div className="flex items-center justify-between mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 h-11 w-80 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <button
              onClick={() => openCreateModal(activeTab === 'teachers' ? 'teacher' : 'student')}
              className="flex items-center gap-2 px-4 py-2.5 bg-indigo-700 hover:bg-indigo-800 text-white font-medium rounded-lg transition-all"
            >
              <UserPlus className="w-5 h-5" />
              Create {activeTab === 'teachers' ? 'Teacher' : 'Student'}
            </button>
          </div>
        )}

        {/* Content */}
        {activeTab === 'teachers' && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Name</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Email</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Subjects</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Status</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredTeachers.map(teacher => (
                  <tr key={teacher.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 text-sm font-medium text-slate-900">{teacher.name}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{teacher.email}</td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {(teacher.subject_ids || []).map(sid => {
                          const sub = subjects.find(s => s.id === sid);
                          return sub ? (
                            <span key={sid} className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded">
                              {sub.name}
                            </span>
                          ) : null;
                        })}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        teacher.is_active !== false
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {teacher.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEditModal(teacher)}
                          className="p-2 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all"
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openResetPasswordModal(teacher)}
                          className="p-2 text-slate-500 hover:text-amber-600 hover:bg-amber-50 rounded transition-all"
                          title="Reset Password"
                        >
                          <KeyRound className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => toggleUserActive(teacher.id, teacher.is_active !== false)}
                          className="p-2 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all"
                          title={teacher.is_active !== false ? 'Deactivate' : 'Activate'}
                        >
                          {teacher.is_active !== false ? (
                            <ToggleRight className="w-4 h-4" />
                          ) : (
                            <ToggleLeft className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => deleteUser(teacher.id, teacher.name)}
                          className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredTeachers.length === 0 && (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-slate-500">
                      No teachers found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'students' && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Name</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Email</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Board</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Grade</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Subjects</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Status</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredStudents.map(student => {
                  const board = boards.find(b => b.id === student.board_id);
                  const grade = grades.find(g => g.id === student.grade_id);
                  return (
                    <tr key={student.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 text-sm font-medium text-slate-900">{student.name}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{student.email}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{board?.name || '-'}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{grade?.name || '-'}</td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {(student.subject_ids || []).map(sid => {
                            const sub = subjects.find(s => s.id === sid);
                            return sub ? (
                              <span key={sid} className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded">
                                {sub.name}
                              </span>
                            ) : null;
                          })}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          student.is_active !== false
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {student.is_active !== false ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => openEditModal(student)}
                            className="p-2 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all"
                            title="Edit"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openResetPasswordModal(student)}
                            className="p-2 text-slate-500 hover:text-amber-600 hover:bg-amber-50 rounded transition-all"
                            title="Reset Password"
                          >
                            <KeyRound className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => toggleUserActive(student.id, student.is_active !== false)}
                            className="p-2 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all"
                            title={student.is_active !== false ? 'Deactivate' : 'Activate'}
                          >
                            {student.is_active !== false ? (
                              <ToggleRight className="w-4 h-4" />
                            ) : (
                              <ToggleLeft className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => deleteUser(student.id, student.name)}
                            className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filteredStudents.length === 0 && (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-slate-500">
                      No students found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'settings' && (
          <SettingsTab
            boards={boards}
            grades={grades}
            subjects={subjects}
            onRefresh={fetchData}
          />
        )}
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900">
                Create New {createUserType === 'teacher' ? 'Teacher' : 'Student'}
              </h2>
            </div>
            
            <form onSubmit={handleCreateUser} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Full Name</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Password</label>
                <input
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="********"
                />
              </div>

              {/* Student-specific fields */}
              {createUserType === 'student' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Board</label>
                    <select
                      value={formData.board_id}
                      onChange={(e) => setFormData({ ...formData, board_id: e.target.value })}
                      className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Select Board</option>
                      {boards.map(b => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Grade</label>
                    <select
                      value={formData.grade_id}
                      onChange={(e) => setFormData({ ...formData, grade_id: e.target.value })}
                      className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Select Grade</option>
                      {grades.map(g => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {/* Teacher-specific fields */}
              {createUserType === 'teacher' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Boards (Multiple)</label>
                    <div className="flex flex-wrap gap-2">
                      {boards.map(b => (
                        <button
                          key={b.id}
                          type="button"
                          onClick={() => handleMultiSelect('board_ids', b.id)}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                            formData.board_ids.includes(b.id)
                              ? 'bg-indigo-600 text-white'
                              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                          }`}
                        >
                          {b.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Grades (Multiple)</label>
                    <div className="flex flex-wrap gap-2">
                      {grades.map(g => (
                        <button
                          key={g.id}
                          type="button"
                          onClick={() => handleMultiSelect('grade_ids', g.id)}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                            formData.grade_ids.includes(g.id)
                              ? 'bg-indigo-600 text-white'
                              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                          }`}
                        >
                          {g.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Subjects for both */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Subjects</label>
                <div className="flex flex-wrap gap-2">
                  {subjects.map(s => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => handleSubjectToggle(s.id)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        formData.subject_ids.includes(s.id)
                          ? 'bg-amber-600 text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      {s.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-indigo-700 hover:bg-indigo-800 text-white font-medium rounded-lg transition-all"
                >
                  Create {createUserType === 'teacher' ? 'Teacher' : 'Student'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900">
                Edit {editingUser.role === 'teacher' ? 'Teacher' : 'Student'}: {editingUser.name}
              </h2>
              <p className="text-sm text-slate-500 mt-1">{editingUser.email}</p>
            </div>
            
            <form onSubmit={handleEditUser} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Full Name</label>
                <input
                  type="text"
                  required
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Student-specific fields */}
              {editingUser.role === 'student' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Board</label>
                    <select
                      value={editFormData.board_id}
                      onChange={(e) => setEditFormData({ ...editFormData, board_id: e.target.value })}
                      className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Select Board</option>
                      {boards.map(b => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Grade</label>
                    <select
                      value={editFormData.grade_id}
                      onChange={(e) => setEditFormData({ ...editFormData, grade_id: e.target.value })}
                      className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Select Grade</option>
                      {grades.map(g => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {/* Teacher-specific fields */}
              {editingUser.role === 'teacher' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Boards (Multiple)</label>
                    <div className="flex flex-wrap gap-2">
                      {boards.map(b => (
                        <button
                          key={b.id}
                          type="button"
                          onClick={() => handleEditMultiSelect('board_ids', b.id)}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                            editFormData.board_ids.includes(b.id)
                              ? 'bg-indigo-600 text-white'
                              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                          }`}
                        >
                          {b.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Grades (Multiple)</label>
                    <div className="flex flex-wrap gap-2">
                      {grades.map(g => (
                        <button
                          key={g.id}
                          type="button"
                          onClick={() => handleEditMultiSelect('grade_ids', g.id)}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                            editFormData.grade_ids.includes(g.id)
                              ? 'bg-indigo-600 text-white'
                              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                          }`}
                        >
                          {g.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Subjects for both */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Subjects</label>
                <div className="flex flex-wrap gap-2">
                  {subjects.map(s => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => handleEditSubjectToggle(s.id)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        editFormData.subject_ids.includes(s.id)
                          ? 'bg-amber-600 text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      {s.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowEditModal(false); setEditingUser(null); }}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-indigo-700 hover:bg-indigo-800 text-white font-medium rounded-lg transition-all"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && resetPasswordUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900">Reset Password</h2>
              <p className="text-sm text-slate-500 mt-1">
                Set a new password for <span className="font-medium text-slate-700">{resetPasswordUser.name}</span>
              </p>
              <p className="text-xs text-slate-400 mt-0.5">{resetPasswordUser.email}</p>
            </div>
            
            <form onSubmit={handleResetPassword} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">New Password</label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="Minimum 6 characters"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowResetPasswordModal(false); setResetPasswordUser(null); setNewPassword(''); }}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg transition-all"
                >
                  Reset Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Settings Tab Component
const SettingsTab = ({ boards, grades, subjects, onRefresh }) => {
  const [newBoard, setNewBoard] = useState('');
  const [newGrade, setNewGrade] = useState('');
  const [newSubject, setNewSubject] = useState('');

  const addBoard = async (e) => {
    e.preventDefault();
    if (!newBoard.trim()) return;
    try {
      await api.post('/admin/boards', { name: newBoard.trim() });
      toast.success('Board added');
      setNewBoard('');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add board');
    }
  };

  const addGrade = async (e) => {
    e.preventDefault();
    if (!newGrade.trim()) return;
    try {
      await api.post('/admin/grades', { name: newGrade.trim() });
      toast.success('Grade added');
      setNewGrade('');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add grade');
    }
  };

  const addSubject = async (e) => {
    e.preventDefault();
    if (!newSubject.trim()) return;
    try {
      await api.post('/admin/subjects', { name: newSubject.trim() });
      toast.success('Subject added');
      setNewSubject('');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add subject');
    }
  };

  const deleteBoard = async (id, name) => {
    if (!window.confirm(`Delete board "${name}"?`)) return;
    try {
      await api.delete(`/boards/${id}`);
      toast.success('Board deleted');
      onRefresh();
    } catch (err) {
      toast.error('Failed to delete board');
    }
  };

  const deleteGrade = async (id, name) => {
    if (!window.confirm(`Delete grade "${name}"?`)) return;
    try {
      await api.delete(`/grades/${id}`);
      toast.success('Grade deleted');
      onRefresh();
    } catch (err) {
      toast.error('Failed to delete grade');
    }
  };

  const deleteSubject = async (id, name) => {
    if (!window.confirm(`Delete subject "${name}"?`)) return;
    try {
      await api.delete(`/subjects/${id}`);
      toast.success('Subject deleted');
      onRefresh();
    } catch (err) {
      toast.error('Failed to delete subject');
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {/* Boards */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
            <Layers className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Boards</h2>
            <p className="text-sm text-slate-500">{boards.length} boards</p>
          </div>
        </div>
        <form onSubmit={addBoard} className="flex gap-2 mb-4">
          <input
            type="text"
            value={newBoard}
            onChange={(e) => setNewBoard(e.target.value)}
            placeholder="e.g., IGCSE"
            className="flex-1 h-10 px-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
          />
          <button
            type="submit"
            className="h-10 w-10 flex items-center justify-center bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg"
          >
            <Plus className="w-5 h-5" />
          </button>
        </form>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {boards.map(b => (
            <div key={b.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="font-medium text-slate-700">{b.name}</span>
              <button
                onClick={() => deleteBoard(b.id, b.name)}
                className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Grades */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
            <GraduationCap className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Grades</h2>
            <p className="text-sm text-slate-500">{grades.length} grades</p>
          </div>
        </div>
        <form onSubmit={addGrade} className="flex gap-2 mb-4">
          <input
            type="text"
            value={newGrade}
            onChange={(e) => setNewGrade(e.target.value)}
            placeholder="e.g., Grade 9"
            className="flex-1 h-10 px-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
          />
          <button
            type="submit"
            className="h-10 w-10 flex items-center justify-center bg-green-600 hover:bg-green-700 text-white rounded-lg"
          >
            <Plus className="w-5 h-5" />
          </button>
        </form>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {grades.map(g => (
            <div key={g.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="font-medium text-slate-700">{g.name}</span>
              <button
                onClick={() => deleteGrade(g.id, g.name)}
                className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Subjects */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Subjects</h2>
            <p className="text-sm text-slate-500">{subjects.length} subjects</p>
          </div>
        </div>
        <form onSubmit={addSubject} className="flex gap-2 mb-4">
          <input
            type="text"
            value={newSubject}
            onChange={(e) => setNewSubject(e.target.value)}
            placeholder="e.g., Physics"
            className="flex-1 h-10 px-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
          />
          <button
            type="submit"
            className="h-10 w-10 flex items-center justify-center bg-amber-600 hover:bg-amber-700 text-white rounded-lg"
          >
            <Plus className="w-5 h-5" />
          </button>
        </form>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {subjects.map(s => (
            <div key={s.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="font-medium text-slate-700">{s.name}</span>
              <button
                onClick={() => deleteSubject(s.id, s.name)}
                className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
